<template>
  <div>
    <slot name="greeting-message" />
    <q-list separator>
      <q-item
        v-if="$q.screen.gt.xs"
        v-ripple
        clickable
        to="/account"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('auth:account', 'fas fa-grid-2-plus', 'fal fa-grid-2-plus')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('pages.account.accountOverview') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/my-membership"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:my-membership', 'fas fa-id-card', 'fal fa-id-card')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $tc('membership.membership', 1) }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('membership.viewAndManageMembershipPrompt') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/account-information"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:account-information', 'fas fa-user', 'fal fa-user')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('pages.account.accountInformation') }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('pages.account.editYourContactInformationPrompt') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/orders"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:orders', 'fas fa-ticket', 'fal fa-ticket')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('order.orderHistory') }}
            <q-badge
              v-if="orders.currentAndUpcomingOrders.length"
              color="primary"
              align="middle"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white': 'dark'"
              :label="orders.currentAndUpcomingOrders.length"
              rounded
            />
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('order.viewCurrentAndPastPurchasesPrompt') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/update-payment-method"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:update-payment-method', 'fas fa-credit-card', 'fal fa-credit-card')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('payment.paymentInformation') }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('payment.paymentInfoPrompt') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/my-movies"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:my-movies', 'fas fa-camera-movie', 'fal fa-camera-movie')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('movie.myMovies') }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('movie.editYourWatchlistAndFavoriteMovies') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-ripple
        clickable
        to="/marketing-preferences"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:manage-marketing-preferences', 'fas fa-paper-plane-top', 'fal fa-paper-plane-top')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('user.actions.manageMarketingPreferences') }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('user.manageContactMethod') }}
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-if="currentCircuit.allowFriends"
        v-ripple
        clickable
        to="/manage-friends"
      >
        <q-item-section avatar>
          <q-icon :name="getIcon('consumer:manage-friends', 'fas fa-user-group', 'fal fa-user-group')" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('user.actions.manageFriends') }}
          </q-item-label>
          <q-item-label class="text-body2">
            {{ $t('pages.manageFriends.addFriendsShortenPrompt') }}…
          </q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-if="currentUser && $q.screen.xs"
        v-ripple
        clickable
        to="/logout"
        :class="$q.dark.isActive ? undefined : 'text-negative'"
        :style="{ color: $q.dark.isActive ? `${$lightenColor(currentSite.negativeColor, 50)} !important` : undefined }"
      >
        <q-item-section avatar>
          <q-icon name="fal fa-arrow-right-from-bracket" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1 text-bold text-capitalize">
            {{ $t('auth.actions.logout') }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>

<script>
export default {
  name: 'AccountNavigation',
  props: {
    hiddenGreetingMessage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'currentCircuit'],
  models: {
    orders: {
      orderItems: {
        ticket: {
          showing: 'default'
        }
      },
      force: true,
      variables () {
        return {
          state: 'not_draft',
          limit: 10,
          userId: this.currentUser?.id,
          noCount: true
        }
      }
    }
  },
  computed: {
    genericRouteNameParts () {
      const parts = _.split(this.$route.name, ':')
      return [_.first(parts), _.last(parts)]
    }
  },
  methods: {
    getTextColor (color) {
      return this.$isColorDark(color) ? 'white' : 'black'
    },
    editUser () {
      this.$refs.memberForm.show(this.currentUser)
      this.showMemberForm = true
    },
    getIcon (iconRouteName, activeIcon, defaultIcon) {
      // Ignore any middle :site-${id}: namespace, e.g. 'consumer:site-1:my-membership'
      let iconRouteNamePartsParts = _.split(iconRouteName, ':')
      iconRouteNamePartsParts = [_.first(iconRouteNamePartsParts), _.last(iconRouteNamePartsParts)]
      return _.isEqual(iconRouteNamePartsParts, this.genericRouteNameParts) ? activeIcon : defaultIcon
    }
  }
}
</script>
